import { BufferManager } from '../memory/BufferManager';

const DEFAULT_ALIGNMENT = 1;

/**
 * @typedef {Object} WebGLBufferDescriptor
 * @property {GLenum} target - The target to which the buffer is bound.
 * @property {GLsizeiptr} size - Default size of the buffer in bytes.
 * @property {GLenum} usage - Usage flags for the buffer.
 */

/**
 * Thin wrapper around BufferManager that uses WebGL buffers.
 */
export class BufferManagerWebGL {
  /** @type {WebGLRenderingContext} */
  #gl;

  /** @type {BufferManager} */
  #bufferManager;

  /** @type {WebGLBufferDescriptor} */
  #bufferDesc;

  /**
   * @param {WebGLRenderingContext} gl - WebGL rendering context.
   * @param {WebGLBufferDescriptor} bufferDesc - Buffer descriptor.
   */
  constructor(gl, bufferDesc) {
    this.#gl = gl;
    this.#bufferDesc = bufferDesc;

    const createBufferCallback = (size) => {
      const buffer = this.#gl.createBuffer();
      this.#gl.bindBuffer(bufferDesc.target, buffer);
      this.#gl.bufferData(bufferDesc.target, size, bufferDesc.usage);
      return buffer;
    };

    const destroyBufferCallback = (buffer) => {
      this.#gl.deleteBuffer(buffer);
    };

    this.#bufferManager = new BufferManager(bufferDesc.size, createBufferCallback, destroyBufferCallback);
  }

  /**
   * Destroys all buffers and clears the free lists.
   * This should be called when the BufferManager is no longer needed.
   */
  dtor() {
    this.#bufferManager.dtor();
    this.#bufferDesc = null;
    this.#gl = null;
  }

  /**
   * Allocates a block of memory with the specified size and alignment.
   * Creates a new buffer if necessary.
   *
   * @param {number} size - Size of the memory block to allocate in bytes
   * @param {number} [alignment=DEFAULT_ALIGNMENT] - Alignment requirement in bytes
   * @returns {MemoryBlock} Allocated memory block
   */
  allocate(size, alignment = DEFAULT_ALIGNMENT) {
    return this.#bufferManager.allocate(size, alignment);
  }

  /**
   * Allocates a buffer and creates a buffer object with all necessary properties.
   *
   * @param {number} size - The size in bytes to allocate.
   * @param {number} strideInBytes - The stride in bytes for alignment.
   * @returns {Object} Buffer object with memblock, getGlBuffer, getStride, size, and offset properties.
   */
  allocateBuffer(size, strideInBytes) {
    const memblock = this.allocate(size, strideInBytes);
    return {
      size: size,
      offset: memblock.offset,
      memblock: memblock,
      bufferManager: this,
      getGlBuffer: () => memblock.buffer,
      getStride: () => strideInBytes
    };
  }

  /**
   * Either frees the block immediately or adds it to the batched list.
   *
   * @param {MemoryBlock} block - The memory block to free
   */
  free(block) {
    this.#bufferManager.free(block);
  }

  /**
   * Flushes all batched free blocks.
   */
  flushFreeBlocks() {
    this.#bufferManager.flushFreeBlocks();
  }

  /**
   * Remove all buffers from the cache (since those were all deallocated when the context was lost)
   */
  resetAfterContextLoss() {
    this.#bufferManager.resetAfterContextLoss();
  }

  /**
   * Gets the total size of all allocated buffers.
   *
   * @returns {number} Total size in bytes
   */
  getTotalSize() {
    return this.#bufferManager.getTotalSize();
  }

  /**
   * Gets the size of a single buffer entry.
   *
   * @returns {number} Buffer size in bytes
   */
  getBufferSize() {
    return this.#bufferManager.getBufferSize();
  }

  /**
   * Gets the total amount of free space across all buffers.
   *
   * @returns {number} Total free space in bytes
   */
  getTotalFreeSpace() {
    return this.#bufferManager.getTotalFreeSpace();
  }

  /**
   * Gets the total amount of used space across all buffers.
   *
   * @returns {number} Total used space in bytes
   */
  getUsedBytes() {
    return this.#bufferManager.getUsedBytes();
  }
}